/**
 * Onboarding async-request lifecycle. The naming is confusing, so to be precise:
 * an `OnboardRequest` is the asynchronous request entity (status persisted in DynamoDB); the
 * `requestOnboard` mutation *triggers* one (returns `IN_PROGRESS`), and the `onboardRequest` query
 * *polls* its status until it leaves `IN_PROGRESS`. On `COMPLETED` the access token is refreshed so
 * it carries the NetSuite customer id.
 */
import { ApolloClient, ApolloError, DefaultContext, QueryOptions } from '@apollo/client'
import { bind, SUSPENSE } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concat,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  ignoreElements,
  interval,
  map,
  merge,
  mergeWith,
  NEVER,
  Observable,
  of,
  race,
  sample,
  scan,
  Subject,
  switchMap,
  take,
  takeWhile,
  timer,
} from 'rxjs'

import {
  OnboardRequestDocument,
  OnboardRequestFragment,
  OnboardRequestInput,
  OnboardRequestQuery,
  RequestOnboardDocument,
} from '../../graphql/search'
import { isNotNull } from '../../utils/collectionTools'
import { CustomError, logAndCaptureException } from '../../utils/errorTools'
import { isResultEqual, left, LeftResult, right, RightResult } from '../../utils/result'
import { coerceCaughtToLeftWith } from '../../utils/rx/errors'
import {
  retryWithBackoff,
  switchMapLeft,
  switchMapRight,
  tapLeft,
  tapRight,
} from '../../utils/rx/operators'
import { EmptyObject } from '../../utils/types'
import { setAnalyticsEvent } from '../analytics/state'
import { refreshAccessToken } from '../auth/expiration'
import { decodeTokenCustomerId } from '../auth/scope'
import { AuthStateWithAccessToken, latestAuthStateWithAccessToken$ } from '../auth/state'
import { latestCommaClient$ } from '../graphql/apollo'
import { commaQueryFactory, SearchQueryResult } from '../search/query'
import {
  buildOnboardRequestInput,
  extractActiveOnboardingForOnboardRequest,
  isEmailVerificationSentError,
} from './utils'

const [onboardRequestInputChange$, setOnboardRequestInput] = createSignal<OnboardRequestInput>()
// create hook for sharing latest input pending subscription to request observable
export const [useLatestOnboardRequestInput, latestOnboardRequestInput$] = bind(
  onboardRequestInputChange$,
  null,
)

export { setOnboardRequestInput }

const triggerRetryWithExistingInput$ = new Subject<void>()

/**
 * Trigger retry when existing input available from `latestOnboardRequestInput$`. This is
 * important when the onboarding claim in auth state is outdated (typically the case on the
 * confirmation page since we do not wait for an updated user object from Auth0).
 */
export const triggerRetryWithExistingInput = () => triggerRetryWithExistingInput$.next()

export class OnboardRequestQueryError extends CustomError {
  override name = 'OnboardRequestQueryError'
}

export class OnboardRequestRetryError extends CustomError {
  override name = 'OnboardRequestRetryError'
}

export class RequestOnboardMutationError extends CustomError {
  override name = 'RequestOnboardMutationError'
}

export interface OnboardRequestState {
  loading: boolean
  previousRequest: OnboardRequestFragment | null
  result:
    | LeftResult<OnboardRequestQueryError | OnboardRequestRetryError | RequestOnboardMutationError>
    | RightResult<OnboardRequestQuery['onboardRequest']>
    | null // when loading or not eligible (e.g. customer id present in access token)
}

const onboardRequestQuery = (
  options: Omit<QueryOptions<EmptyObject, OnboardRequestQuery>, 'query'>,
) => {
  const query$ = commaQueryFactory(OnboardRequestDocument)(options)
  return map((result: SearchQueryResult<OnboardRequestQuery>) =>
    result._tag === 'Right'
      ? right(result.data.onboardRequest)
      : left(new OnboardRequestQueryError(result.error.message, result.error)),
  )(query$)
}

const refreshAccessTokenCount = new BehaviorSubject(0)

const retryFailedCount = new BehaviorSubject(0)

const requestOnboardMutation = (
  client: ApolloClient<unknown>,
  token: string,
  input: OnboardRequestInput,
) =>
  defer(() =>
    client.mutate({
      mutation: RequestOnboardDocument,
      variables: {
        input,
        wait: false,
      },
      context: { token } as DefaultContext,
      errorPolicy: 'all',
    }),
  ).pipe(
    // retry network errors
    retryWithBackoff(),
    map(({ data, errors }) => {
      const firstError = errors?.[0]
      const error = firstError ? new ApolloError({ graphQLErrors: [firstError] }) : undefined
      if (error) {
        return left(new RequestOnboardMutationError(error.message, error))
      }
      return right(data?.requestOnboard)
    }),
    tapLeft(({ error }) => logAndCaptureException(error)),
    catchError(
      coerceCaughtToLeftWith((error) => new RequestOnboardMutationError(error.message, error)),
    ),
  )

/**
 * Track number of requests to disable the `onboardRequestQuery` watcher. Use a counter instead
 * of a boolean value to support re-entrant requests.
 */
const watchOnboardRequestQueryDisableCount$ = new BehaviorSubject(0)

/**
 * Update `watchOnboardRequestQueryEnabled$` to `false` - use with `concat()` and restore by
 * calling `enableWatchOnboardRequestQuery` in `finalize()`.
 *
 * ```
 * concat(
 *   disableWatchOnboardRequestQuery$,
 *   someOtherObservable$.pipe(
 *     finalize(enableWatchOnboardRequestQuery)
 *   )
 * )
 * ```
 */
const disableWatchOnboardRequestQuery$: Observable<never> =
  watchOnboardRequestQueryDisableCount$.pipe(
    take(1),
    switchMap((count) => {
      watchOnboardRequestQueryDisableCount$.next(count + 1)
      return EMPTY
    }),
  )

const enableWatchOnboardRequestQuery = () => {
  watchOnboardRequestQueryDisableCount$.pipe(take(1)).subscribe((count) => {
    watchOnboardRequestQueryDisableCount$.next(Math.max(0, count - 1))
  })
}

/**
 * Send `requestOnboard` mutation for given input and subsequently poll `onboardRequest` query
 * until the status changes from `IN_PROGRESS`. If successful, refresh access token.
 */
const requestOnboardAndPollForCompletion$ = (
  client: ApolloClient<unknown>,
  token: string,
  input: OnboardRequestInput,
) =>
  concat(
    disableWatchOnboardRequestQuery$,
    of(SUSPENSE),
    requestOnboardMutation(client, token, input).pipe(
      tapRight(() => {
        const userType = input.patient
          ? 'patient'
          : input.practitioner
            ? 'practitioner'
            : input.student
              ? 'student'
              : ''
        setAnalyticsEvent({
          event: 'sign_up',
          user_type: userType,
        })
      }),
      switchMapRight((mutationResult) =>
        mutationResult.data?.status !== 'IN_PROGRESS'
          ? of(mutationResult)
          : concat(
              of(mutationResult),
              // mutation sent - poll onboardRequest query until status is !== `IN_PROGRESS` (up to 2 min)
              race(
                interval(3000).pipe(
                  switchMap(() => onboardRequestQuery({ fetchPolicy: 'network-only' })),
                  takeWhile(
                    (result) => result._tag === 'Left' || result.data?.status === 'IN_PROGRESS',
                    true,
                  ),
                ),
                timer(120000).pipe(
                  map(() => {
                    const error = new OnboardRequestQueryError(
                      `Query request polling after mutation timed out after 2 minutes`,
                    )
                    logAndCaptureException(error)
                    return left(error)
                  }),
                ),
              ),
            ),
      ),
      switchMapRight((result) =>
        result.data?.status === 'COMPLETED'
          ? // onboard request completed - refresh token
            concat(
              of(result),
              refreshAccessTokenCount.pipe(
                take(1),
                switchMap((count) => {
                  // increment to avoid an unnecessary second refresh from edge-case logic that
                  // refreshes the access token once if status is `COMPLETED` but customer id is
                  // not found in the access token
                  refreshAccessTokenCount.next(count + 1)
                  return refreshAccessToken()
                }),
                ignoreElements(),
              ),
            )
          : of(result),
      ),
      finalize(enableWatchOnboardRequestQuery),
    ),
  )

/**
 * Query for existing `OnboardRequest` with extended retry if the request fails. Then continue
 * observing the query, emitting changes from the Apollo Client cache.
 */
const existingOnboardRequestQuery$ = concat(
  disableWatchOnboardRequestQuery$,
  onboardRequestQuery({}).pipe(
    take(1),
    switchMapLeft((result) => {
      logAndCaptureException(result.error)
      // emit error result and keep trying (up to 2 min) until query succeeds
      return concat(
        of(result),
        race(
          interval(30000).pipe(
            switchMap(() => onboardRequestQuery({}).pipe(take(1))),
            takeWhile(({ _tag }) => _tag === 'Left', true),
          ),
          timer(12000).pipe(
            map(() => {
              const error = new OnboardRequestQueryError(
                `Query request retry timed out after 2 minutes`,
              )
              logAndCaptureException(error)
              return left(error)
            }),
          ),
        ),
      )
    }),
    finalize(enableWatchOnboardRequestQuery),
  ),
  // continue watching OnboardRequest query for changes (when not disabled due to active
  // operation)
  watchOnboardRequestQueryDisableCount$.pipe(
    switchMap((disableCount) =>
      disableCount < 1
        ? onboardRequestQuery({})
        : // IMPORTANT: emit nothing when not enabled (will interrupt poll after mutation
          // otherwise)
          NEVER,
    ),
  ),
).pipe(distinctUntilChanged(isResultEqual))

/**
 * Determine if authenticated user has an active onboarding claim in `complete` state
 */
const hasActiveOnboardingInCompleteState = (
  authState: Extract<AuthStateWithAccessToken, { isAuthenticated: true }>,
): boolean => {
  const extractResult = extractActiveOnboardingForOnboardRequest(authState.user)
  return extractResult._tag === 'Right' && extractResult.data.claim.state === 'complete'
}

/**
 * Retry failed `requestOnboard` mutation once automatically unless OnboardRequestInput is
 * already set or an error was returned indicating that email verification is required. Then
 * retry mutation on manual trigger or input change.
 */
const retryFailedOnceThenOnDemand$ = (
  client: ApolloClient<unknown>,
  authState: Extract<AuthStateWithAccessToken, { isAuthenticated: true }>,
  onboardRequest: OnboardRequestFragment | null | undefined,
) =>
  retryFailedCount.pipe(
    take(1),
    switchMap((count) =>
      latestOnboardRequestInput$.pipe(
        take(1),
        switchMap((latestInput) => {
          retryFailedCount.next(count + 1)
          // retry automatically (once) if and only if (iff):
          // - failure response did not indicate that email verification is required
          // - input not already set (avoid retrying immediately after a failed first
          //    attempt - state in claim isn't up-to-date at this point anyway)
          if (
            count === 0 &&
            !latestInput &&
            !isEmailVerificationSentError(onboardRequest?.response)
          ) {
            // try to build mutation input from active onboarding claim
            const extractResult = extractActiveOnboardingForOnboardRequest(authState.user)
            if (extractResult._tag === 'Left') {
              const error = new OnboardRequestRetryError(extractResult.error)
              logAndCaptureException(error)
              return of(left(error))
            }
            const activeOnboarding = extractResult.data
            const input = buildOnboardRequestInput(activeOnboarding)
            if (!input) {
              const error = new OnboardRequestRetryError(
                `Failed to build onboard request input from: ${JSON.stringify(activeOnboarding)}`,
              )
              logAndCaptureException(error)
              return of(left(error))
            }
            // send requestOnboard mutation and poll query for completion
            return requestOnboardAndPollForCompletion$(client, authState.accessToken, input)
          }

          // we've already retried at least once - wait for manual trigger or input change
          return merge(
            // wait for input change
            onboardRequestInputChange$,
            // OR trigger with existing input (for retry with existing input)
            latestOnboardRequestInput$.pipe(
              filter(isNotNull),
              sample(triggerRetryWithExistingInput$),
            ),
          ).pipe(
            filter(isNotNull),
            // input received - submit
            switchMap((input) =>
              requestOnboardAndPollForCompletion$(client, authState.accessToken, input),
            ),
          )
        }),
      ),
    ),
  )

/**
 * Keep track of the previous request (whose status was not `IN_PROGRESS`) to provide context
 * when retrying (e.g. `loading: true` or status is `IN_PROGRESS`)
 */
const retainPreviousRequest = () =>
  scan<Omit<OnboardRequestState, 'previousRequest'>, OnboardRequestState, null>(
    (acc, v) =>
      // acc is `null` (seed value) at the beginning
      acc == null
        ? {
            ...v,
            previousRequest:
              v.result?._tag === 'Right' && v.result.data?.status !== 'IN_PROGRESS'
                ? v.result.data ?? null
                : null,
          }
        : v.loading ||
            !v.result ||
            v.result._tag === 'Left' ||
            v.result.data?.status === 'IN_PROGRESS'
          ? { ...v, previousRequest: acc.previousRequest }
          : { ...v, previousRequest: v.result.data ?? null },
    null,
  )

/**
 * Query and poll onboard request state (if any), trigger new request when input submitted, retry
 * an existing failed request once automatically, and retry failed request on demand.
 *
 * - Emits `OnboardRequest` fragment in result as updates occur.
 * - Emits `{ loading: true, result: null }` when kicking off a new request, followed by
 *   `{ loading: false, result: { ... } }` as updates to the request become available.
 */
export const [useOnboardRequestState, onboardRequestState$] = bind<OnboardRequestState>(
  combineLatest([
    latestCommaClient$,
    latestAuthStateWithAccessToken$, // does not emit until authentication state has settled
  ]).pipe(
    switchMap(([client, authState]) => {
      // if not authenticated or customer id already present, we have nothing to do
      if (!authState.isAuthenticated || !!decodeTokenCustomerId(authState.accessToken)) {
        return of({ loading: false, result: null })
      }
      // start with query for existing onboard request
      return existingOnboardRequestQuery$.pipe(
        switchMapRight((queryResult) => {
          // determine next steps based on the existing onboard request (if any)
          if (queryResult.data?.status === 'COMPLETED') {
            // onboard query status is `COMPLETED`, but the access token is missing the customer id - refresh once
            return concat(
              of(queryResult),
              refreshAccessTokenCount.pipe(
                filter((count) => count === 0),
                take(1),
                switchMap((count) => {
                  refreshAccessTokenCount.next(count + 1)
                  return refreshAccessToken()
                }),
                ignoreElements(),
              ),
            )
          }
          if (queryResult.data || hasActiveOnboardingInCompleteState(authState)) {
            // Onboard query exists but did not succeed OR onboard query does not exist even though
            // user has an active onboarding claim in `complete` state. The latter can happen if
            // the `requestOnboard` mutation were to fail for some reason. Retry once and then wait
            // for input (manual retry).
            return concat(
              of(queryResult),
              retryFailedOnceThenOnDemand$(client, authState, queryResult.data),
            )
          }
          // onboard query not found - wait for input
          return concat(
            of(queryResult),
            onboardRequestInputChange$.pipe(
              filter(isNotNull),
              // input received - submit
              switchMap((input) =>
                requestOnboardAndPollForCompletion$(client, authState.accessToken, input),
              ),
            ),
          )
        }),
        map((result) =>
          // SUSPENSE marks the beginning of `requestOnboardAndPollForCompletion$`
          result === SUSPENSE ? { loading: true, result: null } : { loading: false, result },
        ),
      )
    }),
    retainPreviousRequest(),
    mergeWith(
      // subscribe to `latestOnboardRequestInputChange$` so we can re-use input for manual retry
      latestOnboardRequestInput$.pipe(ignoreElements()),
    ),
  ),
  { loading: true, previousRequest: null, result: null },
)
